import React from "react";
import { Box, Card, Chip, Stack, Typography } from "@mui/material";

/**
 * Compact "Upgrade to Premium" prompt — used on surfaces (My Plan tab, the
 * Onboarding step revisited after picking Basic) where the user is already
 * confirmed as Basic and just needs the upsell. Matches Nick's mock: title +
 * subtitle on the left, two mini cards (Monthly outlined / Yearly purple-filled
 * with Best Value chip) on the right.
 *
 * @param {{
 *   selectedCadence?: 'monthly' | 'yearly',
 *   onChooseMonthly?: () => void,
 *   onChooseYearly?: () => void,
 * }} props
 */
const UpgradeToPremium = (props) => {
  const { selectedCadence, onChooseMonthly, onChooseYearly } = props;
  return (
  <Card
    elevation={0}
    sx={{
      border: "1px solid #e2e8f0",
      borderRadius: 3,
      p: { xs: 2.5, md: 3 },
    }}
  >
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={{ xs: 2.5, md: 3 }}
      alignItems={{ xs: "stretch", md: "center" }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            color: "#1e293b",
            fontSize: "1.5rem",
            lineHeight: 1.2,
          }}
        >
          Upgrade to Premium
        </Typography>
        <Typography sx={{ color: "#6b7280", mt: 0.75, fontSize: "0.95rem" }}>
          Choose how you want to pay. You can change or cancel later.
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ flexShrink: 0 }}
      >
        {/* Monthly — purple border + light purple background when selected. */}
        <Card
          elevation={0}
          onClick={onChooseMonthly}
          sx={{
            border:
              selectedCadence === "monthly"
                ? "2px solid #712d94"
                : "1px solid #e2e8f0",
            backgroundColor:
              selectedCadence === "monthly" ? "rgba(113, 45, 148, 0.04)" : "#fff",
            borderRadius: 2.5,
            px: 2.5,
            py: 1.5,
            minWidth: 180,
            cursor: onChooseMonthly ? "pointer" : "default",
            transition: "all 0.2s ease",
            "&:hover": onChooseMonthly
              ? { borderColor: "#712d94", boxShadow: "0 4px 12px rgba(113, 45, 148, 0.15)" }
              : {},
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1rem" }}>
            Monthly
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.875rem", mt: 0.25 }}>
            £3.99/month
          </Typography>
        </Card>

        {/* Yearly (Best Value) — soft yellow ring by default to signal the
            recommended option; explicit selection swaps to a vivid yellow
            border so it's clearly distinct from the default state. */}
        <Card
          elevation={0}
          onClick={onChooseYearly}
          sx={{
            backgroundColor: "#712d94",
            color: "#fff",
            borderRadius: 2.5,
            px: 2.5,
            py: 1.5,
            minWidth: 220,
            position: "relative",
            overflow: "visible",
            cursor: onChooseYearly ? "pointer" : "default",
            transition: "all 0.2s ease",
            border:
              selectedCadence === "yearly"
                ? "3px solid #FFC107"
                : "3px solid transparent",
            boxShadow:
              selectedCadence === "yearly"
                ? "0 0 0 4px rgba(255, 193, 7, 0.45), 0 6px 18px rgba(113, 45, 148, 0.35)"
                : selectedCadence === undefined
                  ? "0 0 0 3px rgba(255, 193, 7, 0.55)"
                  : "none",
            "&:hover": onChooseYearly
              ? { boxShadow: "0 4px 14px rgba(113, 45, 148, 0.3)" }
              : {},
          }}
        >
          <Chip
            label="Best Value"
            sx={{
              position: "absolute",
              top: -10,
              right: 12,
              backgroundColor: "#FFC107",
              color: "#000",
              fontWeight: 700,
              fontSize: "0.7rem",
              height: 24,
              "& .MuiChip-label": { px: 1.2 },
            }}
          />
          <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: "1rem" }}>
            Yearly
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.875rem", mt: 0.25 }}>
            £34.99/year · Save 27%
          </Typography>
        </Card>
      </Stack>
    </Stack>
  </Card>
  );
};

export default UpgradeToPremium;
